import { BrainCircuit, Calendar, ChartNoAxesColumn, Network, Phone } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../../components/ui/badge";
import { ActionMenu } from "../../components/ui/action-menu";
import { useToast } from "../../components/ui/toast";
import { formatDuration } from "../../lib/time";
import { cn } from "../../lib/utils";
import { MoveAgentModal } from "./move-agent-modal";
import { RenameAgentModal } from "./rename-agent-modal";
import { DeleteAgentModal } from "./delete-agent-modal";
import type { Agent, AgentAnalytics } from "./types";
import { useAuth } from "../../context/AuthContext";
import { canManageAgents } from "../../lib/userAccess";
import { getAgentAnalyticsPath, getAgentSchedulePath } from "./card-routes";

const health = (r: number) =>
  r >= 90 ? "var(--color-success)" : r >= 60 ? "var(--color-warning)" : "var(--color-danger)";

export function AgentCard({ agent, stats, onTestCall }: {
  agent: Agent; stats?: AgentAnalytics; onTestCall: (a: Agent) => void;
}) {
  const agentName = agent.agent_name || agent.name || "Untitled Agent";
  const displayAgentName = agentName.length > 50 ? agentName.slice(0, 50) + "..." : agentName;
  const nav = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const canEdit = canManageAgents(user);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const s: AgentAnalytics = stats ?? {
    agent_id: agent.agent_id,
    total_calls: 0,
    connected_calls: 0,
    failed_calls: 0,
    unique_reached: 0,
    unique_contacts: 0,
  };
  // Lifetime counter off the agent document. `stats` comes from the analytics
  // endpoint, which defaults to a 7-day window -- using it here made the card
  // read 0 for any agent idle for a week.
  const calls = agent.total_calls ?? s.total_calls ?? 0;
  // Same figure the Analytics page shows as "Connectivity": unique contacts
  // reached / unique contacts attempted, so retries of one contact don't count
  // as separate misses. Falls back to per-attempt only if the backend omits it.
  const windowCalls = s.total_calls || 0;
  const successRate = s.unique_connectivity_percentage != null
    ? Math.round(s.unique_connectivity_percentage)
    : windowCalls > 0
      ? Math.round(((s.unique_reached ?? s.connected_calls ?? 0) / (s.unique_contacts || windowCalls || 1)) * 100)
      : 0;
  const isMultiAgent = agent.agent_type?.toLowerCase() === "multi" || !!agent.multi_agent_config_id;
  const isThinkerTalker = agent.agent_type?.toLowerCase() === "thinker_talker";
  const schedulePath = getAgentSchedulePath(agent);
  const analyticsPath = getAgentAnalyticsPath(agent);

  return (
    <div
      onClick={() => { if (canEdit) nav(`/home/create-agent?agent_id=${agent.agent_id}`); }}
      className={cn(
        "agent-card group flex flex-col p-4 transition-all hover:-translate-y-0.5 hover:shadow-card-hover shadow-card dark:shadow-card-dark bg-paper-card dark:bg-[#292524]",
        canEdit ? "cursor-pointer" : "cursor-default"
      )}
    >
      <div className="flex items-start gap-2.5">
        <span className={cn(
          "flex h-9 w-9 items-center justify-center rounded-lg bg-soft font-semibold text-ink text-sm",
          isMultiAgent    && "bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-400",
          isThinkerTalker && "bg-accent-50 text-accent-500 dark:bg-accent-500/10 dark:text-accent-400",
        )}>
          {isThinkerTalker ? <BrainCircuit className="h-4 w-4" /> : isMultiAgent ? <Network className="h-4 w-4" /> : agentName.slice(0, 1)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-start gap-2">
            <p className="line-clamp-2 break-words text-sm font-semibold text-ink" title={agentName}>{displayAgentName}</p>
            {isMultiAgent && <Badge tone="violet" className="shrink-0 mt-0.5">Multi-agent</Badge>}
            {isThinkerTalker && <Badge tone="brand" className="shrink-0 mt-0.5">Thinker-Talker</Badge>}
          </div>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <ActionMenu ariaLabel={`${agentName} actions`} items={[
            { label: "Test Call", onClick: () => onTestCall(agent) },
            { label: "Schedule", onClick: () => nav(schedulePath) },
            { label: "Analytics", onClick: () => nav(analyticsPath) },
            { label: "Move to Folder", onClick: () => setShowMoveModal(true) },
            { label: "Rename", onClick: () => setShowRenameModal(true) },
            { label: "Delete", danger: true, onClick: () => setShowDeleteModal(true) },
          ]} />
        </div>
      </div>

      <p className="line-clamp-2 min-h-[2.5rem] mt-2.5 text-sm text-ink-muted">{agent.description ?? " "}</p>

      <div className="my-2.5 grid grid-cols-3 divide-x divide-line border-y border-line py-2">
        {[
          { v: calls.toLocaleString("en-IN"), l: "CALLS" },
          { v: `${successRate}%`, l: "SUCCESS", c: health(successRate) },
          { v: formatDuration(stats?.avg_duration_seconds || 0), l: "AVG LEN" },
        ].map((x) => (
          <div key={x.l} className="px-2 first:pl-0">
            <p className="font-mono text-sm font-semibold tabular-nums" style={{ color: x.c ?? "var(--color-ink)" }}>{x.v}</p>
            <p className="font-mono text-[9px] uppercase tracking-wide text-ink-faint">{x.l}</p>
          </div>
        ))}
      </div>

      <div className="h-1 overflow-hidden rounded-full bg-line">
        <div className="h-full rounded-full" style={{ width: `${successRate}%`, background: health(successRate) }} />
      </div>

      <div className="mt-3 flex items-center gap-1.5 border-t border-line pt-2.5" onClick={(e) => e.stopPropagation()}>
        <button onClick={() => onTestCall(agent)} className="flex items-center gap-1 rounded-md bg-emerald-50 text-emerald-700 hover:bg-emerald-100/80 border border-emerald-200/60 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20 px-2.5 py-1 text-xs font-medium transition-colors">
          <Phone className="h-3 w-3" /> Test Call
        </button>
        <button
          onClick={() => nav(schedulePath)}
          className="flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium text-ink shadow-[0_0_0_1px_rgba(28,25,23,0.08)] dark:shadow-[0_0_0_1px_rgba(255,255,255,0.08)] dark:text-stone-100 hover:bg-accent-50 hover:text-accent-700 dark:hover:bg-accent-500/10 dark:hover:text-accent-400 transition-colors"
        >
          <Calendar className="h-3 w-3" /> Schedule
        </button>
        <button aria-label="Analytics" onClick={() => nav(analyticsPath)} className="flex h-6 w-6 items-center justify-center rounded-md bg-soft text-ink-muted hover:bg-line hover:text-ink transition-colors">
          <ChartNoAxesColumn className="h-3 w-3" />
        </button>
      </div>

      {showMoveModal && <MoveAgentModal agent={agent} onClose={() => setShowMoveModal(false)} />}
      {showRenameModal && <RenameAgentModal agent={agent} onClose={() => setShowRenameModal(false)} />}
      {showDeleteModal && <DeleteAgentModal agent={agent} onClose={() => setShowDeleteModal(false)} />}
    </div>
  );
}
